import { assertFmEvidenceDescriptor } from './FmEvidence.validation';
import { assertDashboardRegionRecipe } from './dashboardOverviewRecipes';
import { validateDashboardAssistantAction } from './FmDashboardOverview.assistantValidation';
import {
  dashboardExact as exact,
  dashboardRecord as record,
  dashboardRegisterId as registerId,
  dashboardString as string,
  dashboardValidationFail as fail,
  type DashboardUnknownRecord as UnknownRecord,
} from './FmDashboardOverview.validationHelpers';
import {
  array,
  COLUMN_ROLES,
  MEDIA_ENUMS,
  optionalStrings,
  strings,
  validateAction,
} from './FmDashboardOverview.validationShared';

export function validateState(value: unknown, path: string, ids: Set<string>): string {
  const state = record(value, path);
  const status = string(state.status, `${path}.status`);
  const fieldSets: Record<string, string[]> = {
    ready: [],
    loading: ['label', 'detail'],
    partial: ['label', 'detail'],
    empty: ['label', 'detail', 'action'],
    stale: ['label', 'detail', 'updatedAt'],
    locked: ['label', 'detail', 'action'],
    error: ['label', 'detail', 'retry'],
  };
  const fields = fieldSets[status];
  if (!fields)
    fail(`${path}.status`, 'must be ready, loading, empty, partial, stale, locked, or error');
  exact(state, ['status', ...fields], path);
  strings(
    state,
    path,
    fields.filter((field) => field !== 'action' && field !== 'retry'),
  );
  if (status === 'empty' && state.action !== undefined)
    validateAction(state.action, `${path}.action`, ids);
  if (status === 'locked') validateAction(state.action, `${path}.action`, ids);
  if (status === 'error') validateAction(state.retry, `${path}.retry`, ids);
  return status;
}

export function validateMedia(value: unknown, path: string): void {
  const media = record(value, path);
  exact(
    media,
    [
      'scope',
      'species',
      'developmentClass',
      'landClass',
      'structure',
      'condition',
      'reportedCause',
      'basisLabel',
      'sourceRevision',
      'freshness',
      'actualMedia',
      'allowIllustrativeFallback',
      'stateId',
    ],
    path,
  );
  Object.entries(MEDIA_ENUMS).forEach(([key, values]) => {
    if (media[key] !== undefined && !values.has(media[key] as never)) {
      fail(`${path}.${key}`, 'contains an unsupported value');
    }
  });
  if (typeof media.allowIllustrativeFallback !== 'boolean') {
    fail(`${path}.allowIllustrativeFallback`, 'must be a boolean');
  }
  optionalStrings(media, path, ['basisLabel', 'sourceRevision', 'freshness', 'stateId']);
  if (media.actualMedia !== undefined) {
    const actualPath = `${path}.actualMedia`;
    const actual = record(media.actualMedia, actualPath);
    exact(actual, ['identity', 'evidenceKind'], actualPath);
    string(actual.identity, `${actualPath}.identity`);
    if (
      ![
        'field-image',
        'inspection-map',
        'orthophoto',
        'canopy-raster',
        'property-image',
        'other',
      ].includes(String(actual.evidenceKind))
    ) {
      fail(`${actualPath}.evidenceKind`, 'contains an unsupported value');
    }
  }
}

function validateExpertColumn(value: unknown, path: string, ids: Set<string>): UnknownRecord {
  const column = record(value, path);
  exact(column, ['id', 'label', 'value', 'role'], path);
  registerId(column.id, `${path}.id`, ids);
  string(column.label, `${path}.label`);
  if (column.role !== undefined && !COLUMN_ROLES.has(String(column.role))) {
    fail(`${path}.role`, 'contains an unsupported semantic table role');
  }
  const descriptor = record(column.value, `${path}.value`);
  const field = string(descriptor.field, `${path}.value.field`);
  if (field === 'expert') {
    exact(descriptor, ['field', 'key'], `${path}.value`);
    string(descriptor.key, `${path}.value.key`);
  } else {
    exact(descriptor, ['field'], `${path}.value`);
    if (!['rank', 'title', 'state', 'reason', 'change', 'source', 'revision'].includes(field)) {
      fail(`${path}.value.field`, 'contains an unsupported value');
    }
  }
  return column;
}

export function validateExpertTable(
  value: unknown,
  path: string,
  ids: Set<string>,
): UnknownRecord[] {
  const table = record(value, path);
  exact(table, ['caption', 'showLabel', 'hideLabel', 'open', 'columns'], path);
  strings(table, path, ['caption', 'showLabel', 'hideLabel']);
  if (typeof table.open !== 'boolean') fail(`${path}.open`, 'must be a boolean');
  const columns = array(table.columns, `${path}.columns`);
  if (columns.length > 8) fail(`${path}.columns`, 'must contain at most 8 items');
  return columns.map((entry, index) =>
    validateExpertColumn(entry, `${path}.columns[${index}]`, ids),
  );
}

function validateCalmAssistant(
  answer: UnknownRecord,
  rankingRunId: string | null,
  topRecordId: string | null,
  ids: Set<string>,
  handlers: UnknownRecord,
): void {
  if (rankingRunId === null) {
    if (answer.assistantAction !== undefined) {
      fail('props.sections[0].assistantAction', 'must be absent without a served ranking identity');
    }
    if (topRecordId !== null) {
      fail('props.sections[0].topRecordId', 'must be null without a served ranking identity');
    }
    return;
  }
  const action = validateDashboardAssistantAction(
    answer.assistantAction,
    'props.sections[0].assistantAction',
    ids,
    handlers,
  );
  const context = record(action.context, 'props.sections[0].assistantAction.context');
  if (action.tool !== 'explain' || context.scope !== 'portfolio') {
    fail('props.sections[0].assistantAction', 'must be a portfolio explain action');
  }
  if (context.rankingRunId !== rankingRunId) {
    fail(
      'props.sections[0].assistantAction.context.rankingRunId',
      'must match answer rankingRunId',
    );
  }
  if (context.topRecordId !== topRecordId) {
    fail('props.sections[0].assistantAction.context.topRecordId', 'must match answer topRecordId');
  }
}

export function validateHandlers(value: unknown): UnknownRecord {
  const root = record(value, 'props.handlers');
  exact(
    root,
    ['onPrimaryAction', 'onExpertViewChange', 'onMapVisibilityChange', 'assistantTools'],
    'props.handlers',
  );
  if (
    root.onMapVisibilityChange !== undefined &&
    typeof root.onMapVisibilityChange !== 'function'
  ) {
    fail('props.handlers.onMapVisibilityChange', 'must be a callback');
  }
  if (typeof root.onPrimaryAction !== 'function') {
    fail('props.handlers.onPrimaryAction', 'must be a callback');
  }
  if (typeof root.onExpertViewChange !== 'function') {
    fail('props.handlers.onExpertViewChange', 'must be a callback');
  }
  const handlers = record(root.assistantTools, 'props.handlers.assistantTools');
  exact(handlers, ['explain', 'navigate'], 'props.handlers.assistantTools');
  Object.entries(handlers).forEach(([key, handler]) => {
    if (typeof handler !== 'function') {
      fail(`props.handlers.assistantTools.${key}`, 'must be a callback');
    }
  });
  return handlers;
}

export function validateHeaderActions(value: unknown, ids: Set<string>): void {
  if (value === undefined) return;
  const actions = array(value, 'props.headerActions');
  const primaryCount = actions.filter((entry, index) => {
    const path = `props.headerActions[${index}]`;
    validateAction(entry, path, ids);
    return record(entry, path).intent === 'primary';
  }).length;
  if (primaryCount !== 1) {
    fail('props.headerActions', 'must contain exactly one primary action');
  }
}

export function validateCalmAnswerSection(
  answer: UnknownRecord,
  ids: Set<string>,
  handlers: UnknownRecord,
): { rankingRunId: string | null; topRecordId: string | null } {
  exact(
    answer,
    [
      'kind',
      'id',
      'ownerQuestion',
      'literalAnswer',
      'state',
      'rankingRunId',
      'topRecordId',
      'assistantAction',
      'recipe',
      'utilityActionGroupLabel',
      'utilityActions',
      'evidence',
    ],
    'props.sections[0]',
  );
  if (answer.kind !== 'calm-answer') fail('props.sections[0].kind', 'must be calm-answer');
  registerId(answer.id, 'props.sections[0].id', ids);
  strings(answer, 'props.sections[0]', ['ownerQuestion', 'literalAnswer']);
  assertDashboardRegionRecipe(answer.recipe, 'props.sections[0].recipe', {
    dominantBeat: 'literal-answer',
    primaryAction: 'top-record-primary-action',
    expertReveal: 'none',
    conversionRole: 'orientation',
  });
  validateState(answer.state, 'props.sections[0].state', ids);
  if ((answer.utilityActions === undefined) !== (answer.utilityActionGroupLabel === undefined)) {
    fail('props.sections[0]', 'must provide utilityActions and utilityActionGroupLabel together');
  }
  if (answer.utilityActions !== undefined) {
    string(answer.utilityActionGroupLabel, 'props.sections[0].utilityActionGroupLabel');
    const actions = array(answer.utilityActions, 'props.sections[0].utilityActions');
    const primaryCount = actions.filter((entry, index) => {
      const path = `props.sections[0].utilityActions[${index}]`;
      validateAction(entry, path, ids);
      return record(entry, path).intent === 'primary';
    }).length;
    if (primaryCount !== 1) {
      fail('props.sections[0].utilityActions', 'must contain exactly one primary utility action');
    }
  }
  if (answer.evidence !== undefined) assertFmEvidenceDescriptor(answer.evidence);
  const rankingRunId =
    answer.rankingRunId === null
      ? null
      : string(answer.rankingRunId, 'props.sections[0].rankingRunId');
  const topRecordId =
    answer.topRecordId === null
      ? null
      : string(answer.topRecordId, 'props.sections[0].topRecordId');
  validateCalmAssistant(answer, rankingRunId, topRecordId, ids, handlers);
  return { rankingRunId, topRecordId };
}
